import { Box, Typography } from "@mui/material";
import { AdvancedSettingsBar } from "components/Campaign/CampaignEffects/AdvancedSettingsBar/AdvancedSettingsBar";
import { FormSection } from "components/FormControls/FormSection";
import { ReactComponent as Edit03Icon } from "components/Icons/edit-03.svg";
import { OLTabs } from "components/Tabs/OLTabs";
import { useMessage } from "hooks/intl";
import { has } from "lodash";
import { useMemo, useState } from "react";
import { AchievementFEFormat } from "types/achievements";
import { AchievementStepsIds, AchievementStepsUnion } from "types/stepper";
import { getStringValue } from "utilities/strings";
import { AchievementLimit } from "../../SingleAchievement/Limit";
import { useStyles } from "../AchievementForm.styles";
import { GoalDescription } from "../Rules/RuleGoal/RuleGoalReadOnly";
import {
  DetailsList,
  getTriggerLabel,
  prepareAchievementLimitToBEFormat,
} from "./helpers";

interface Props {
  errors?: any;
  data: AchievementFEFormat;
  skipToStep: (id: AchievementStepsUnion) => void;
}

export const RulesSummary = ({ errors, data, skipToStep }: Props) => {
  const classes = useStyles();
  const { message } = useMessage();

  const [activeTab, setActiveTab] = useState(0);

  const achievementRulesNumber = data?.rules?.length || 0;

  const tabs = [...Array(achievementRulesNumber)].map((_, index) => {
    const ruleData = data.rules[index];
    const ruleTranslation = ruleData?.translations[0];
    const ruleName = ruleTranslation?.name
      ? ruleTranslation.name
      : message({ id: "ruleNumber" }, { number: index + 1 });

    return {
      id: index.toString(),
      name: ruleName,
      isError: errors?.rules && !!errors?.rules[index],
    };
  });

  const selectedRule = data?.rules[activeTab];

  const customEventTriggerLabels = [
    {
      label: message({ id: "countUniqueCustomEventAttributes" }),
      value: "",
    },
    {
      label: message({ id: "customEventAttribute" }),
      value: selectedRule.completeRule?.uniqueAttribute || "",
    },
  ];

  const ruleDetails = useMemo(
    () => [
      {
        left: message({ id: "ruleName" }),
        right: <Typography>{tabs[activeTab].name}</Typography>,
      },
      {
        left: message({ id: "ruleDescription" }),
        right: (
          <Typography>
            {getStringValue(selectedRule.translations[0]?.description)}
          </Typography>
        ),
      },
      {
        left: message({ id: "ruleType" }),
        right: message({ id: selectedRule.type }),
      },
      {
        left: message({ id: "ruleTrigger" }),
        right: message({ id: getTriggerLabel(selectedRule?.trigger) }),
        error: errors?.rules?.[activeTab]?.event,
      },
      {
        left: message({ id: "goal" }),
        right: !!selectedRule.completeRule?.periodGoal ? (
          <>
            <GoalDescription
              completeRule={selectedRule.completeRule}
              trigger={selectedRule.trigger}
              event={selectedRule?.event}
              aggregation={selectedRule.aggregation}
            />
            {selectedRule.completeRule?.onlyUniqueAttributeValues &&
              !!selectedRule.completeRule?.uniqueAttribute && (
                <>
                  <Box mt={10} />
                  <AdvancedSettingsBar
                    variant="text"
                    labels={customEventTriggerLabels}
                    vectorMarginClass={classes.vectorMargin}
                    advancedSettingsClass={classes.goalAdvancedSettings}
                  />
                </>
              )}
          </>
        ) : (
          "-"
        ),
        rightClassName: classes.goalDescription,
        error: errors?.rules?.[activeTab]?.completeRule,
      },
      {
        left: message({ id: "eventLimit" }),
        right: !has(selectedRule, "limit") ? (
          "-"
        ) : (
          <AchievementLimit
            {...prepareAchievementLimitToBEFormat(selectedRule?.limit)}
          />
        ),
        error: errors?.rules?.[activeTab]?.limit,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedRule, activeTab, tabs],
  );

  return (
    <FormSection>
      <Box display="flex" justifyContent="space-between" mb={8}>
        <Typography fontWeight={500} mb={0}>
          {message({ id: "rulesSummary" })}
        </Typography>
        <Edit03Icon
          onClick={() => skipToStep(AchievementStepsIds.RULES)}
          className={classes.editIcon}
        />
      </Box>
      <OLTabs
        items={tabs}
        onChange={(_, newValue: string) => {
          setActiveTab(Number(newValue));
        }}
        value={activeTab.toString()}
        maxLabelLength={12}
        className={classes.rulesTabs}
      />
      <DetailsList detailsList={ruleDetails} />
    </FormSection>
  );
};
